/*
  Herfur product page.

  Owns the buy panel and the gallery. Like the homepage showcase it deliberately
  does not extend Dawn's <variant-selects> / <product-form> custom elements —
  the markup is Herfur's own — but it hands off to Dawn's cart notification or
  drawer after an add, so the post-add experience matches the rest of the store.

  It also stays out of the bundle app's way. The quantity is the only thing the
  tier tiles touch; pricing, discounts and cart rules belong to the app, and
  every change is announced as an event on the section root so the app (or a
  pixel) can listen instead of being patched into this file.
*/

(function () {
  /*
    Which cart sections to ask Shopify to re-render depends on which cart UI the
    theme is set to. Dawn's drawer wants ['cart-drawer', 'cart-icon-bubble'];
    the notification wants its own three. Hardcoding one meant flipping the
    cart_type setting silently broke the post-add experience, so ask the host
    element what it needs — both implement getSectionsToRender().
  */
  function cartHost() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function cartSectionIds() {
    var host = cartHost();
    if (host && typeof host.getSectionsToRender === 'function') {
      return host.getSectionsToRender().map(function (section) {
        return section.id;
      });
    }
    return ['cart-icon-bubble'];
  }


  function HerfurProduct(root) {
    this.root = root;

    var dataEl = root.querySelector('[data-herfur-variant-data]');
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!this.variants || !this.variants.length) return;

    this.form = root.querySelector('[data-herfur-form]');
    this.variantInput = root.querySelector('[data-herfur-variant-input]');
    this.qtyInput = root.querySelector('[data-herfur-qty-input]');
    this.priceEl = root.querySelector('[data-herfur-price]');
    this.addButton = root.querySelector('[data-herfur-add]');
    this.buyNowButton = root.querySelector('[data-herfur-buy-now]');
    this.addLabel = root.querySelector('[data-herfur-add-label]');
    this.errorEl = root.querySelector('[data-herfur-error]');
    this.stockEl = root.querySelector('[data-herfur-stock]');
    this.sticky = root.querySelector('[data-herfur-sticky]');
    this.stickyPrice = root.querySelector('[data-herfur-sticky-price]');
    this.fieldsets = Array.prototype.slice.call(root.querySelectorAll('[data-option-index]'));
    this.colourIndex = root.dataset.colourIndex === '' ? -1 : parseInt(root.dataset.colourIndex, 10);
    this.addLabelText = this.addLabel ? this.addLabel.textContent.trim() : 'Add to cart';

    this.bindOptions();
    this.bindBuilder();
    this.bindBundle();
    this.bindQuantity();
    this.bindGallery();
    this.bindSizeGuide();
    this.bindSubmit();
    this.bindSticky();
    this.update();
  }

  /* ------------------------------------------------------------- variants */

  /*
    Keyed off data-option-index, not DOM order: the panel shows colour before
    size while the product itself has size as option 1, and variant.options is
    in the product's order. Reading position from the markup would silently
    match the wrong variant.
  */
  HerfurProduct.prototype.selectedOptions = function () {
    var selection = [];

    this.fieldsets.forEach(function (fieldset) {
      var index = parseInt(fieldset.dataset.optionIndex, 10);
      if (isNaN(index)) return;
      var checked = fieldset.querySelector('[data-herfur-option-input]:checked');
      selection[index] = checked ? checked.value : null;
    });

    // Fill any hole — Array#every skips them, which would match any variant
    for (var i = 0; i < selection.length; i++) {
      if (selection[i] === undefined) selection[i] = null;
    }

    return selection;
  };

  HerfurProduct.prototype.matchVariant = function (selection) {
    return (
      this.variants.find(function (variant) {
        return selection.every(function (value, index) {
          return value === null || variant.options[index] === value;
        });
      }) || null
    );
  };

  HerfurProduct.prototype.bindOptions = function () {
    var self = this;
    this.root.querySelectorAll('[data-herfur-option-input]').forEach(function (input) {
      input.addEventListener('change', function () {
        self.update();
      });
    });
  };

  /*
    In "build your collection" mode there are no option pickers on the page —
    the builder owns the buy panel. It still tells us which colour the first bra
    is, so the gallery can follow along.
  */
  HerfurProduct.prototype.bindBuilder = function () {
    var self = this;
    this.root.addEventListener('herfur:collection:colour', function (event) {
      if (event.detail && event.detail.colour) self.showColour(event.detail.colour);
    });
  };

  // Grey out the values that lead nowhere for the current selection. They stay
  // clickable — disabling them strands a shopper on a dead end.
  HerfurProduct.prototype.markAvailability = function (selection) {
    var self = this;

    this.fieldsets.forEach(function (fieldset) {
      var optionIndex = parseInt(fieldset.dataset.optionIndex, 10);
      if (isNaN(optionIndex)) return;

      var label = fieldset.querySelector('[data-herfur-option-label]');
      if (label && selection[optionIndex]) label.textContent = selection[optionIndex];

      fieldset.querySelectorAll('[data-herfur-option-input]').forEach(function (input) {
        var probe = selection.slice();
        probe[optionIndex] = input.value;

        var reachable = self.variants.some(function (variant) {
          if (!variant.available) return false;
          return probe.every(function (value, index) {
            return value === null || variant.options[index] === value;
          });
        });

        var chip = fieldset.querySelector('[data-herfur-option-chip="' + input.id + '"]');
        if (chip) chip.classList.toggle('is-unavailable', !reachable);
      });
    });
  };

  HerfurProduct.prototype.update = function () {
    var selection = this.selectedOptions();
    var variant = this.matchVariant(selection);

    this.markAvailability(selection);

    if (this.colourIndex >= 0 && selection[this.colourIndex]) {
      this.showColour(selection[this.colourIndex]);
    } else {
      /*
        Builder mode: there are no option fieldsets, so the colour comes from
        the first bra's swatch, which Liquid pre-checks. Read rather than wait
        for the builder's event, so the gallery is already on that colourway on
        the first paint no matter which script initialises first.
      */
      var builderColour = this.root.querySelector('[data-herfur-colour]:checked');
      if (builderColour) this.showColour(builderColour.value);
    }

    this.updatePackTiles(selection);

    if (!variant) {
      this.setAddState(false, 'unavailable');
      return;
    }

    if (this.variantInput) this.variantInput.value = variant.id;

    if (this.priceEl) {
      var html = '<span class="herfur-product__price-current">' + variant.price + '</span>';
      if (variant.compare_at_price) {
        html += '<s class="herfur-product__price-was">' + variant.compare_at_price + '</s>';
      }
      this.priceEl.innerHTML = html;
    }

    this.variantPrice = variant.price;
    this.syncStickyPrice();

    this.setAddState(variant.available, 'soldout');
    this.updateStock(variant);
    this.updateUrl(variant);
    this.hideError();

    this.emit('herfur:variant:change', { variant: variant, quantity: this.quantity() });
  };

  // Keep ?variant= in step so a refresh, a share or the bundle app reading the
  // URL all land on the colour and size she actually chose.
  HerfurProduct.prototype.updateUrl = function (variant) {
    if (!window.history || !window.history.replaceState || !this.root.dataset.updateUrl) return;

    // Builder mode has no option pickers, so there is no chosen variant to put
    // in the URL — stamping the first one there would claim a choice she hasn't
    // made, and send anyone she shares the link with to that variant.
    if (!this.fieldsets.length) return;

    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  };

  HerfurProduct.prototype.setAddState = function (available, reason) {
    if (this.addButton) this.addButton.disabled = !available;
    if (this.stickyAdd) this.stickyAdd.disabled = !available;

    if (!this.addLabel) return;

    if (available) {
      this.addLabel.textContent = this.addLabelText;
    } else {
      this.addLabel.textContent =
        reason === 'unavailable'
          ? this.root.dataset.unavailableText || 'Unavailable'
          : this.root.dataset.soldOutText || 'Sold out';
    }
  };

  /*
    Fill a stock line's text, with the number itself wrapped so it can carry the
    gold while the sentence around it stays body copy.

    Assembled from nodes rather than innerHTML. The template is theme copy today
    and injecting it would be harmless today — but building markup from a string
    is the habit that eventually gets handed a value that isn't.

    There is a second copy of this in herfur-bundle-builder.js, which fills the
    per-bra line. Ten duplicated lines beat a shared file: this store's product
    page is already carrying four scripts, and a fifth request for a function
    this size costs more on a phone than the duplication does. Change one, check
    the other.
  */
  function fillStockText(el, template, count) {
    var parts = String(template).split('[count]');

    el.textContent = '';
    parts.forEach(function (part, index) {
      if (index > 0) {
        var mark = document.createElement('span');
        mark.className = 'herfur-stock__count';
        mark.textContent = count;
        el.appendChild(mark);
      }
      if (part) el.appendChild(document.createTextNode(part));
    });
  }

  HerfurProduct.prototype.updateStock = function (variant) {
    if (!this.stockEl) return;

    var threshold = parseInt(this.stockEl.dataset.threshold || '6', 10);
    var show = variant.available && variant.tracked && variant.inventory > 0 && variant.inventory <= threshold;

    this.stockEl.hidden = !show;
    if (!show) return;

    var template = this.stockEl.dataset.template || 'Only [count] left';

    /*
      Into the text span, never onto the element itself — the pulsing dot is a
      sibling, and setting textContent on the parent would delete it. The
      fallback keeps this working if the mark is ever rendered without one.
    */
    var target = this.stockEl.querySelector('[data-herfur-stock-text]') || this.stockEl;
    fillStockText(target, template, variant.inventory);

    // Down to the last piece is a different message from "running low", and the
    // mark says so on its own: faster pulse, full-strength ink.
    var last = variant.inventory === 1;
    this.stockEl.classList.toggle('herfur-stock--last', last);
    this.stockEl.classList.toggle('herfur-stock--low', !last);
  };

  /* --------------------------------------------------------------- bundle */

  /*
    The tiles are a quantity picker and nothing more. Whatever the tile says the
    bundle costs is the merchant's text and the app's job to honour — this only
    tells the form how many to add and announces the change.
  */
  HerfurProduct.prototype.bindBundle = function () {
    var self = this;
    var inputs = this.root.querySelectorAll('[data-herfur-bundle-input]');
    if (!inputs.length) return;

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        self.setQuantity(parseInt(input.dataset.quantity, 10) || 1);
        self.syncStickyPrice();
        self.emit('herfur:bundle:change', { quantity: self.quantity() });
      });
    });

    var checked = this.root.querySelector('[data-herfur-bundle-input]:checked');
    if (checked) this.setQuantity(parseInt(checked.dataset.quantity, 10) || 1);
  };

  /*
    Pack-option mode: each tile quotes the real variant for that pack at the
    size and colour currently selected, so changing colour can never leave a
    stale price on a tile. Nothing is calculated here — the price and the saving
    both come from Shopify.
  */
  HerfurProduct.prototype.updatePackTiles = function (selection) {
    var self = this;
    var pack = this.root.querySelector('[data-herfur-pack]');
    if (!pack) return;

    var packIndex = parseInt(pack.dataset.optionIndex, 10);
    if (isNaN(packIndex)) return;

    var template = this.root.dataset.saveTemplate || 'Save [amount]';

    pack.querySelectorAll('[data-herfur-pack-value]').forEach(function (input) {
      var probe = selection.slice();
      probe[packIndex] = input.value;

      var variant = self.matchVariant(probe);
      var tile = pack.querySelector('[data-herfur-option-chip="' + input.id + '"]');
      if (!tile) return;

      var priceEl = tile.querySelector('[data-herfur-tier-price]');
      if (priceEl) priceEl.textContent = variant ? variant.price : '';

      var saveEl = tile.querySelector('[data-herfur-tier-save]');
      if (!saveEl) return;

      if (variant && variant.savings) {
        saveEl.textContent = template.replace('[amount]', variant.savings);
        saveEl.hidden = false;
      } else {
        saveEl.textContent = '';
        saveEl.hidden = true;
      }
    });
  };

  /*
    The sticky bar quotes the selected tier, not the unit price — the panel no
    longer shows a unit price at all, so a bar that said PKR 2,299 while the
    3-pack was selected would be quoting something the shopper never chose.
    Falls back to the variant price when there are no tiers.
  */
  HerfurProduct.prototype.syncStickyPrice = function () {
    if (!this.stickyPrice) return;

    var checked = this.root.querySelector('[data-herfur-bundle-input]:checked');
    var tierPrice = checked ? checked.dataset.price : '';

    this.stickyPrice.textContent = tierPrice || this.variantPrice || '';
  };

  HerfurProduct.prototype.quantity = function () {
    return this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
  };

  HerfurProduct.prototype.setQuantity = function (value) {
    if (!this.qtyInput) return;
    this.qtyInput.value = value < 1 ? 1 : value;
  };

  HerfurProduct.prototype.bindQuantity = function () {
    var self = this;
    this.root.querySelectorAll('[data-herfur-qty]').forEach(function (button) {
      button.addEventListener('click', function () {
        var step = parseInt(button.dataset.herfurQty, 10);
        self.setQuantity(self.quantity() + step);
      });
    });
  };

  /* -------------------------------------------------------------- gallery */

  HerfurProduct.prototype.bindGallery = function () {
    var self = this;

    this.track = this.root.querySelector('[data-herfur-track]');
    if (!this.track) return;

    var gallery = this.root.querySelector('[data-herfur-gallery]');
    this.galleryMode = (gallery && gallery.dataset.colourMedia) || 'scroll';

    this.slides = Array.prototype.slice.call(this.track.querySelectorAll('.herfur-gallery__slide'));
    this.thumbs = Array.prototype.slice.call(this.root.querySelectorAll('.herfur-gallery__thumb'));
    this.prevButton = this.root.querySelector('[data-herfur-gallery-prev]');
    this.nextButton = this.root.querySelector('[data-herfur-gallery-next]');

    this.thumbs.forEach(function (button) {
      button.addEventListener('click', function () {
        self.goToMedia(button.dataset.mediaId);
      });
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', function () {
        self.step(-1);
      });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', function () {
        self.step(1);
      });
    }

    // Swipe and arrow-key scrolling both land here, so the thumbnails follow
    // the track rather than the other way round.
    var ticking = false;
    this.track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          self.syncFromScroll();
          ticking = false;
        });
      },
      { passive: true }
    );

    this.bindZoom();
    this.syncFromScroll();
  };

  HerfurProduct.prototype.visibleSlides = function () {
    return this.slides.filter(function (slide) {
      return !slide.hidden;
    });
  };

  HerfurProduct.prototype.currentIndex = function () {
    var width = this.track.clientWidth;
    if (!width) return 0;
    return Math.round(this.track.scrollLeft / width);
  };

  HerfurProduct.prototype.goTo = function (index, instant) {
    var visible = this.visibleSlides();
    if (!visible.length) return;

    var clamped = Math.max(0, Math.min(index, visible.length - 1));

    /*
      `behavior: 'auto'` means "whatever the stylesheet says", which is
      `scroll-behavior: smooth` on the track and `auto` under
      prefers-reduced-motion. Asking for 'smooth' here instead would force the
      animation on someone who has asked not to have one.

      So an instant jump switches the property off around the call rather than
      arguing with it — without that, moving to a colourway two dozen slides
      away flicks through every one in between, most of them not yet loaded. An
      instant scroll lands synchronously, so it can be handed straight back.
    */
    if (instant) this.track.style.scrollBehavior = 'auto';
    this.track.scrollTo({ left: clamped * this.track.clientWidth, behavior: 'auto' });
    if (instant) this.track.style.scrollBehavior = '';

    this.setActive(clamped);
  };

  HerfurProduct.prototype.goToMedia = function (mediaId) {
    var visible = this.visibleSlides();
    for (var i = 0; i < visible.length; i++) {
      if (visible[i].dataset.mediaId === String(mediaId)) {
        this.goTo(i);
        return;
      }
    }
  };

  HerfurProduct.prototype.step = function (direction) {
    this.goTo(this.currentIndex() + direction);
  };

  HerfurProduct.prototype.syncFromScroll = function () {
    this.setActive(this.currentIndex());
  };

  HerfurProduct.prototype.setActive = function (index) {
    var visible = this.visibleSlides();
    var active = visible[index];
    if (!active) return;

    var mediaId = active.dataset.mediaId;

    this.thumbs.forEach(function (thumb) {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
    });
    if (this.prevButton) this.prevButton.disabled = index <= 0;
    if (this.nextButton) this.nextButton.disabled = index >= visible.length - 1;

    this.revealThumb(mediaId);
  };

  /*
    Keep the active thumbnail inside the rail's scroll window. Deliberately not
    scrollIntoView: that walks every scrollable ancestor, so on first paint it
    would scroll the page itself down to the gallery.
  */
  HerfurProduct.prototype.revealThumb = function (mediaId) {
    var rail = this.root.querySelector('[data-herfur-thumbs]');
    if (!rail) return;

    var thumb = this.thumbs.filter(function (item) {
      return item.dataset.mediaId === mediaId;
    })[0];
    if (!thumb) return;

    var railRect = rail.getBoundingClientRect();
    var thumbRect = thumb.getBoundingClientRect();

    // The rail is a column on desktop and a row on mobile, so nudge whichever
    // axis actually scrolls.
    if (rail.scrollHeight > rail.clientHeight + 1) {
      if (thumbRect.top < railRect.top) {
        rail.scrollTop -= railRect.top - thumbRect.top;
      } else if (thumbRect.bottom > railRect.bottom) {
        rail.scrollTop += thumbRect.bottom - railRect.bottom;
      }
    }

    if (rail.scrollWidth > rail.clientWidth + 1) {
      if (thumbRect.left < railRect.left) {
        rail.scrollLeft -= railRect.left - thumbRect.left;
      } else if (thumbRect.right > railRect.right) {
        rail.scrollLeft += thumbRect.right - railRect.right;
      }
    }
  };

  /*
    Point the gallery at the colour she picked. Two behaviours, set in the
    editor and stamped on the gallery by the snippet:

    'scroll' (the default) — every photograph of every colourway stays in the
    track and the stage jumps to where that colour starts. Someone choosing
    between seven shades wants to see the seven shades, so the range stays
    swipeable and the colour chips act as a way into it.

    'filter' — only that colour's photographs are in the track, plus any image
    not tied to a colourway at all (tagged '-' by the gallery snippet).
  */
  HerfurProduct.prototype.showColour = function (colour) {
    if (!this.track) return;

    // Already on this colourway — bail out rather than re-running and snapping
    // the stage back to the first photo. Picking the same colour for bra 3 as
    // bra 2 shouldn't throw away where she'd scrolled to.
    if (this.activeColour === colour) return;

    // Worked out before anything moves, so a colourway with no photographs of
    // its own leaves the gallery alone instead of emptying it — and doesn't
    // block the next real change, because activeColour isn't recorded yet.
    var index = this.colourStart(colour);
    if (index < 0) return;

    var isSwap = this.activeColour !== undefined;
    this.activeColour = colour;

    /*
      Fade the new set in. Only on a real change of colourway, not on the first
      paint — there's nothing to fade from when the page is still loading, and
      the stage would flash. The transition is suppressed for the swap itself so
      the old photographs don't dissolve; they cut, then the new ones arrive.
    */
    if (isSwap) {
      this.track.style.transition = 'none';
      this.track.style.opacity = '0';
    }

    if (this.galleryMode === 'filter') {
      this.applyColourFilter(colour);
      index = 0;
    } else {
      // The jump is instant, so the photograph has to be there already —
      // lazy loading would otherwise land her on a blank frame. Promoting it
      // here rather than eager-loading all seven front views keeps the page's
      // opening weight where it is.
      this.warmSlide(index);
    }

    this.clearZoom();

    /*
      Always instant. Smooth-scrolling across two dozen slides to reach Saddle
      Up would flick through every other colourway on the way, most of them not
      yet loaded — the fade above is what carries the change instead.
    */
    this.goTo(index, true);

    if (!isSwap) return;

    // Next frame, hand opacity back to the stylesheet so it eases to 1
    var track = this.track;
    window.requestAnimationFrame(function () {
      track.style.transition = '';
      track.style.opacity = '';
    });
  };

  // Where this colourway begins, as an index into the currently visible
  // slides — or -1 when nothing is tagged for it.
  HerfurProduct.prototype.colourStart = function (colour) {
    if (this.galleryMode === 'filter') {
      return this.slides.some(function (slide) {
        return colourMatches(slide, colour);
      })
        ? 0
        : -1;
    }

    var visible = this.visibleSlides();
    for (var i = 0; i < visible.length; i++) {
      // An exact match, not colourMatches: the shared images tagged '-' belong
      // to no colourway, so they are never where one starts.
      if (visible[i].dataset.colour === colour) return i;
    }
    return -1;
  };

  HerfurProduct.prototype.applyColourFilter = function (colour) {
    this.slides.forEach(function (slide) {
      slide.hidden = !colourMatches(slide, colour);
    });
    this.thumbs.forEach(function (thumb) {
      var hide = !colourMatches(thumb, colour);
      thumb.hidden = hide;

      // The <li> carries the width in the mobile strip, so hiding only the
      // button inside it would leave a 6.4rem gap where the thumbnail was.
      var item = thumb.parentElement;
      if (item && item.tagName === 'LI') item.hidden = hide;
    });
  };

  HerfurProduct.prototype.warmSlide = function (index) {
    var slide = this.visibleSlides()[index];
    if (!slide) return;

    var image = slide.querySelector('img');
    if (image && image.loading === 'lazy') image.loading = 'eager';
  };

  function colourMatches(el, colour) {
    var tag = el.dataset.colour;
    return tag === '-' || tag === '' || tag === colour;
  }

  /* ----------------------------------------------------------------- zoom */

  HerfurProduct.prototype.bindZoom = function () {
    var self = this;
    this.zoomQuery = window.matchMedia('(min-width: 990px) and (hover: hover)');

    this.slides.forEach(function (slide) {
      var image = slide.querySelector('img');
      if (!image) return;

      image.addEventListener('click', function (event) {
        if (!self.zoomQuery.matches) return;

        if (slide.classList.contains('is-zoomed')) {
          self.clearZoom();
          return;
        }

        var rect = image.getBoundingClientRect();
        var x = ((event.clientX - rect.left) / rect.width) * 100;
        var y = ((event.clientY - rect.top) / rect.height) * 100;

        self.clearZoom();
        image.style.transformOrigin = x + '% ' + y + '%';
        slide.classList.add('is-zoomed');
      });
    });
  };

  HerfurProduct.prototype.clearZoom = function () {
    this.slides.forEach(function (slide) {
      slide.classList.remove('is-zoomed');
    });
  };

  /* ----------------------------------------------------------- size guide */

  HerfurProduct.prototype.bindSizeGuide = function () {
    var dialog = this.root.querySelector('[data-herfur-size-guide]');
    var openers = this.root.querySelectorAll('[data-herfur-size-guide-open]');
    if (!dialog || !openers.length || typeof dialog.showModal !== 'function') return;

    openers.forEach(function (opener) {
      opener.addEventListener('click', function () {
        dialog.showModal();
      });
    });

    var close = dialog.querySelector('[data-herfur-size-guide-close]');
    if (close) {
      close.addEventListener('click', function () {
        dialog.close();
      });
    }

    // Click outside the panel closes it, the way a shopper expects
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
  };

  /* ------------------------------------------------------------ sticky bar */

  HerfurProduct.prototype.bindSticky = function () {
    if (!this.sticky || !this.addButton || !('IntersectionObserver' in window)) return;

    this.stickyAdd = this.root.querySelector('[data-herfur-sticky-add]');
    var self = this;

    /*
      Two conditions, not one. "The Add to cart button isn't on screen" is also
      true before she has scrolled down to it, which would put the bar over the
      gallery on load. It appears only once the real button has gone past the
      top, and hides again once the buy panel is behind her.
    */
    this.buyPassed = false;
    this.panelInView = false;

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          self.buyPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          self.syncSticky();
        });
      },
      { rootMargin: '0px 0px -20% 0px' }
    ).observe(this.addButton);

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.panelInView = entry.isIntersecting;
        self.syncSticky();
      });
    }).observe(this.root);
  };

  HerfurProduct.prototype.syncSticky = function () {
    if (!this.sticky) return;
    this.sticky.hidden = !(this.buyPassed && this.panelInView);
  };

  /* ----------------------------------------------------------------- cart */

  HerfurProduct.prototype.bindSubmit = function () {
    if (!this.form) return;
    var self = this;

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.addToCart();
    });

    if (this.buyNowButton) {
      this.buyNowButton.addEventListener('click', function () {
        self.addToCart(true);
      });
    }
  };

  /*
    `toCheckout` is the only difference between the two buttons: same POST, same
    quantity, same error handling — one reveals the cart, the other leaves for
    checkout. Keeping it as a flag rather than a second method is what stops the
    two paths drifting apart.
  */
  HerfurProduct.prototype.addToCart = function (toCheckout) {
    var self = this;
    var routes = window.routes || {};
    var addUrl = routes.cart_add_url || '/cart/add';
    var button = toCheckout ? this.buyNowButton : this.addButton;

    if (button) button.classList.add('is-loading');
    this.hideError();

    var body = new FormData(this.form);
    body.append('sections', cartSectionIds().join(','));
    body.append('sections_url', window.location.pathname);

    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (parsed) {
        if (parsed.status) {
          self.showError(parsed.description || parsed.message);
          return;
        }
        self.emit('herfur:cart:add', { items: parsed });

        if (toCheckout) {
          /*
            The loading state is deliberately NOT cleared here. The browser is
            still leaving; letting the button spring back to "Buy it now" while
            it does invites a second click, and a second click adds the items
            again.
          */
          window.location.href = (routes.root || '/') + 'checkout';
          return;
        }

        self.revealCart(parsed);
      })
      .catch(function () {
        // Network or parsing failure — send her to the real cart rather than
        // leaving a button that did nothing.
        window.location.href = routes.cart_url || '/cart';
      })
      .finally(function () {
        if (!toCheckout && button) button.classList.remove('is-loading');
      });
  };

  HerfurProduct.prototype.revealCart = function (parsed) {
    var host = cartHost();

    if (host && typeof host.renderContents === 'function' && parsed.sections) {
      if (typeof host.setActiveElement === 'function' && this.addButton) {
        host.setActiveElement(this.addButton);
      }
      host.renderContents(parsed);
      return;
    }

    window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  };

  HerfurProduct.prototype.showError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || 'Something went wrong. Please try again.';
    this.errorEl.hidden = false;
  };

  HerfurProduct.prototype.hideError = function () {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  };

  HerfurProduct.prototype.emit = function (name, detail) {
    this.root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  };

  /* ----------------------------------------------------------------- init */

  function initHerfurProduct(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.herfur-product').forEach(function (el) {
      if (el.dataset.herfurReady === 'true') return;
      el.dataset.herfurReady = 'true';
      new HerfurProduct(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initHerfurProduct(document);
    });
  } else {
    initHerfurProduct(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initHerfurProduct(event.target);
  });
})();
